import {
  AppUpdateResultCode,
  CapacitorUpdater,
} from '@capgo/capacitor-updater';
import { getDefaultStore } from 'jotai';
import { toast } from 'sonner';

import type { AvailableUpdate } from '@/features/appUpdate/atoms/availableUpdateAtom';
import {
  dismissUpdateVersion,
  isDismissedUpdateVersionSync,
} from '@/features/appUpdate/atoms/dismissedUpdateVersionAtom';
import { startedAutoUpdateVersionAtom } from '@/features/appUpdate/atoms/startedAutoUpdateVersionAtom';

import { useOnAvailableAppUpdateInfo } from './useOnAvailableAppUpdateInfo';
import { useOnFlexibleUpdateDownloaded } from './useOnFlexibleUpdateDownloaded';

async function attemptAutoUpdate(update: AvailableUpdate) {
  if (!update.flexibleUpdateAllowed || !update.version) {
    return false;
  }

  try {
    const { code } = await CapacitorUpdater.startFlexibleUpdate();
    if (code !== AppUpdateResultCode.OK) return;
  } catch {
    return false;
  }

  getDefaultStore().set(startedAutoUpdateVersionAtom, update.version);
}

export function usePromptNativeBuildUpdates() {
  useOnAvailableAppUpdateInfo(async availableUpdate => {
    const version = availableUpdate.version;
    if (!version || isDismissedUpdateVersionSync(version)) {
      return;
    }

    if (await attemptAutoUpdate(availableUpdate)) {
      return;
    }

    toast('Update available', {
      description: `New version is available`,
      action: {
        label: 'Update',
        onClick: () => CapacitorUpdater.openAppStore(),
      },
      duration: Infinity,
      onDismiss: () => {
        dismissUpdateVersion(version);
      },
    });
  });

  useOnFlexibleUpdateDownloaded(() => {
    toast('Update ready', {
      description: 'Restart to apply the update',
      action: {
        label: 'Restart',
        onClick: () => CapacitorUpdater.completeFlexibleUpdate(),
      },
      duration: Infinity,
      onDismiss: () => {
        const store = getDefaultStore();
        const flexibleUpdateVersion = store.get(startedAutoUpdateVersionAtom);
        if (!flexibleUpdateVersion) return;
        dismissUpdateVersion(flexibleUpdateVersion);
      },
    });
  });
}
